/**
 * Checks if value is a plain object (not array, not null, not other special types)
 */
function isPlainObject(value: unknown): value is Record<string, any> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  )
}

/**
 * Deep merge objects recursively
 *
 * @param target - Target object to merge into
 * @param sources - Source objects to merge from
 * @returns Merged object (mutates target)
 *
 * @example
 * const obj1 = { a: 1, b: { c: 2, d: 3 } }
 * const obj2 = { b: { c: 4, e: 5 }, f: 6 }
 * deepMerge(obj1, obj2)
 * // Result: { a: 1, b: { c: 4, d: 3, e: 5 }, f: 6 }
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Array<Partial<T> | undefined>
): T {
  if (!sources.length) return target

  for (const source of sources) {
    if (!source) continue

    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) continue

      const sourceValue = source[key]
      const targetValue = target[key]

      // If both values are plain objects, merge them recursively
      if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
        target[key] = deepMerge<T[Extract<keyof T, string>]>(
          {} as T[Extract<keyof T, string>],
          targetValue,
          sourceValue
        )
      }
      // Otherwise, replace target value with source value (including arrays)
      // Skip if source value is undefined
      else if (sourceValue !== undefined) {
        target[key] = sourceValue as T[Extract<keyof T, string>]
      }
    }
  }

  return target
}
